import { useState, useEffect } from 'react';
import { MapPin, Trash2, Check, Plus, X, Edit2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { winchStartPointsAPI } from '@/api/winchStartPoints';
import { Loading } from '@/components/common/Loading';
import type { WinchStartPoint, CreateWinchStartPointDTO, UpdateWinchStartPointDTO } from '@/types';

/**
 * WinchStartPointManager Component
 * Manage winch service base locations (start points)
 */
export function WinchStartPointManager() {
  const [startPoints, setStartPoints] = useState<WinchStartPoint[]>([]);
  const [activeStartPoint, setActiveStartPoint] = useState<WinchStartPoint | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    loadStartPoints();
  }, []);

  const loadStartPoints = async () => {
    try {
      setLoading(true);
      const response = await winchStartPointsAPI.getAll(true);
      setStartPoints(response.data.data.startPoints || []);
      setActiveStartPoint(response.data.data.activeStartPoint || null);
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'فشل تحميل نقاط البداية');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await winchStartPointsAPI.activate(id);
      toast.success('تم تفعيل نقطة البداية بنجاح');
      loadStartPoints();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'فشل تفعيل نقطة البداية');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('هل أنت متأكد من حذف نقطة البداية هذه؟')) {
      return;
    }

    try {
      await winchStartPointsAPI.delete(id);
      toast.success('تم حذف نقطة البداية بنجاح');
      loadStartPoints();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'لا يمكن حذف نقطة البداية النشطة');
    }
  };

  const handleFormSuccess = () => {
    setShowNewForm(false);
    setEditingId(null);
    loadStartPoints();
  };

  if (loading) {
    return <Loading text="جاري تحميل نقاط البداية..." />;
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="h-8 w-8 text-primary" />
          <h2 className="text-2xl font-bold text-white">إدارة نقاط بداية الونش</h2>
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-dark font-semibold rounded-lg hover:bg-primary/90 transition-all shadow-glow"
        >
          <Plus className="h-5 w-5" />
          إضافة نقطة بداية
        </button>
      </div>

      {/* Active Start Point Card */}
      {activeStartPoint && (
        <div className="card bg-gradient-to-br from-success/20 to-success/10 border-success/30">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-success/30 rounded-lg">
                <MapPin className="h-6 w-6 text-success" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-success">نقطة البداية النشطة</h3>
                <p className="text-sm text-gray-400">النقطة الحالية المستخدمة في حساب المسافات</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-success/30 text-success text-sm font-semibold rounded-full border border-success/50">
              نشط
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400 mb-1">الاسم</p>
              <p className="text-white font-semibold">{activeStartPoint.name}</p>
            </div>
            {activeStartPoint.address && (
              <div>
                <p className="text-gray-400 mb-1">العنوان</p>
                <p className="text-white font-semibold">{activeStartPoint.address}</p>
              </div>
            )}
            <div>
              <p className="text-gray-400 mb-1">الإحداثيات</p>
              <p className="text-white font-mono" dir="ltr">
                {activeStartPoint.latitude.toFixed(6)}, {activeStartPoint.longitude.toFixed(6)}
              </p>
            </div>
            {activeStartPoint.notes && (
              <div>
                <p className="text-gray-400 mb-1">ملاحظات</p>
                <p className="text-white">{activeStartPoint.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New/Edit Form */}
      {(showNewForm || editingId) && (
        <StartPointForm
          editingPoint={editingId ? startPoints.find(sp => sp.id === editingId) : undefined}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowNewForm(false);
            setEditingId(null);
          }}
        />
      )}

      {/* Start Points List */}
      <div className="card">
        <h3 className="text-xl font-bold mb-4 text-white">جميع نقاط البداية</h3>
        
        {startPoints.length === 0 ? (
          <div className="text-center py-12 bg-dark-lighter rounded-lg border border-gray-700">
            <MapPin className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">لا توجد نقاط بداية محفوظة</p>
            <button
              onClick={() => setShowNewForm(true)}
              className="mt-4 px-6 py-2 bg-primary text-dark font-semibold rounded-lg hover:bg-primary/90 transition-all"
            >
              إضافة نقطة بداية الآن
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-dark-lighter">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase">
                    الاسم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase">
                    العنوان
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase">
                    الإحداثيات
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-primary uppercase">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-dark divide-y divide-gray-700">
                {startPoints.map((sp) => (
                  <tr
                    key={sp.id}
                    className={`hover:bg-dark-lighter transition-colors ${
                      sp.isActive ? 'bg-success/5' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-white">{sp.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{sp.address || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs font-mono text-gray-400" dir="ltr">
                        {sp.latitude.toFixed(6)}, {sp.longitude.toFixed(6)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          sp.isActive
                            ? 'bg-success/20 text-success border border-success/30'
                            : 'bg-gray-700 text-gray-400 border border-gray-600'
                        }`}
                      >
                        {sp.isActive ? '✓ نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {!sp.isActive && (
                          <button
                            onClick={() => handleActivate(sp.id)}
                            className="p-2 text-success hover:bg-success/20 rounded-lg transition-colors"
                            title="تفعيل"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setEditingId(sp.id)}
                          className="p-2 text-info hover:bg-info/20 rounded-lg transition-colors"
                          title="تعديل"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(sp.id)}
                          disabled={sp.isActive}
                          className={`p-2 rounded-lg transition-colors ${
                            sp.isActive
                              ? 'text-gray-600 cursor-not-allowed'
                              : 'text-danger hover:bg-danger/20'
                          }`}
                          title={sp.isActive ? 'لا يمكن حذف النقطة النشطة' : 'حذف'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * StartPointForm Component
 * Form for creating or editing a start point
 */
interface StartPointFormProps {
  editingPoint?: WinchStartPoint;
  onSuccess: () => void;
  onCancel: () => void;
}

function StartPointForm({ editingPoint, onSuccess, onCancel }: StartPointFormProps) {
  const [formData, setFormData] = useState<CreateWinchStartPointDTO | UpdateWinchStartPointDTO>({
    name: editingPoint?.name || '',
    address: editingPoint?.address || '',
    latitude: editingPoint?.latitude || 0,
    longitude: editingPoint?.longitude || 0,
    notes: editingPoint?.notes || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'الاسم مطلوب';
    }

    if (formData.latitude === 0 || formData.latitude === undefined) {
      newErrors.latitude = 'خط العرض مطلوب';
    } else if (formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'خط العرض يجب أن يكون بين -90 و 90';
    }

    if (formData.longitude === 0 || formData.longitude === undefined) {
      newErrors.longitude = 'خط الطول مطلوب';
    } else if (formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'خط الطول يجب أن يكون بين -180 و 180';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      if (editingPoint) {
        await winchStartPointsAPI.update(editingPoint.id, formData);
        toast.success('تم تحديث نقطة البداية بنجاح');
      } else {
        await winchStartPointsAPI.create(formData as CreateWinchStartPointDTO);
        toast.success('تم إضافة نقطة البداية بنجاح');
      }
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'فشل حفظ نقطة البداية');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">
          {editingPoint ? 'تعديل نقطة البداية' : 'إضافة نقطة بداية جديدة'}
        </h3>
        <button
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-white hover:bg-dark-lighter rounded-lg transition-all"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              الاسم <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-4 py-2 bg-dark-lighter border rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                errors.name ? 'border-danger' : 'border-gray-700'
              }`}
              placeholder="مثال: 360 Mall"
            />
            {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              العنوان
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-4 py-2 bg-dark-lighter border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all"
              placeholder="360 Mall, Al Zahra, Kuwait"
            />
          </div>

          {/* Latitude */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              خط العرض (Latitude) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              step="0.000001"
              value={formData.latitude || ''}
              onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
              className={`w-full px-4 py-2 bg-dark-lighter border rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                errors.latitude ? 'border-danger' : 'border-gray-700'
              }`}
              placeholder="29.301764"
              dir="ltr"
            />
            {errors.latitude && <p className="mt-1 text-xs text-danger">{errors.latitude}</p>}
          </div>

          {/* Longitude */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              خط الطول (Longitude) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              step="0.000001"
              value={formData.longitude || ''}
              onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
              className={`w-full px-4 py-2 bg-dark-lighter border rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all ${
                errors.longitude ? 'border-danger' : 'border-gray-700'
              }`}
              placeholder="47.990494"
              dir="ltr"
            />
            {errors.longitude && <p className="mt-1 text-xs text-danger">{errors.longitude}</p>}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            ملاحظات
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full px-4 py-2 bg-dark-lighter border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary focus:border-primary transition-all"
            placeholder="ملاحظات إضافية..."
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pt-4">
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-dark font-semibold rounded-lg hover:bg-primary/90 transition-all shadow-glow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="h-4 w-4 border-2 border-dark border-t-transparent rounded-full animate-spin" />
                جاري الحفظ...
              </>
            ) : (
              <>
                <Check className="h-5 w-5" />
                {editingPoint ? 'تحديث' : 'حفظ'}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-6 py-3 bg-dark-lighter text-gray-300 font-semibold rounded-lg border border-gray-700 hover:border-primary transition-all disabled:opacity-50"
          >
            إلغاء
          </button>
        </div>
      </form>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-info/10 border border-info/30 rounded-lg">
        <p className="text-sm text-info">
          💡 <strong>نصيحة:</strong> يمكنك الحصول على الإحداثيات من خرائط Google بالنقر على الموقع ونسخ خط العرض والطول
        </p>
      </div>
    </div>
  );
}
